import { useEffect, useRef, useState, Fragment } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import ExportButton from "../../components/ExportButton";
import PeriodFilter from "../../components/PeriodFilter";
import { exportRowsToXlsx } from "../../lib/exportToXlsx";
import { useAuth } from "../../context/AuthContext";
import { openBackendFile } from "../../lib/download";
import { toast } from "sonner";
import { Plus, Upload, Trash2, Paperclip, Pencil, Send, Eye, ChevronDown, ChevronRight, FileDown } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const EXPENSE_TYPES = ["Travel","Conveyance","Food","Stay/Hotel","Client Meeting Expenses","Printing/Stationery","Miscellaneous"];
const EMPTY = { project_id: "", expense_type: "Travel", amount: "", claim_date: new Date().toISOString().slice(0,10), description: "", gst_included: false, payment_mode: "Cash", from_location: "", to_location: "", distance_km: "", bill_path: "", bill_filename: "" };

export default function Reimbursements() {
  const { user } = useAuth();
  const isMgmt = ["partner","admin","manager"].includes(user?.role);
  const canView = isMgmt || user?.role === "accounts";
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scope, setScope] = useState("mine");
  const [userFilter, setUserFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [clients, setClients] = useState([]);
  const [period, setPeriod] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [activeTab, setActiveTab] = useState("claims");
  const fileRef = useRef(null);

  const toggleExpand = (key) => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key); else next.add(key);
    setExpanded(next);
  };

  const loadSummary = async () => {
    try {
      const parts = [];
      if (startDate) parts.push(`start_date=${startDate}`);
      if (endDate) parts.push(`end_date=${endDate}`);
      const { data } = await api.get(`/reimbursements/summary${parts.length ? "?" + parts.join("&") : ""}`);
      setSummary(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const dateParams = `${startDate ? `&start_date=${startDate}` : ""}${endDate ? `&end_date=${endDate}` : ""}`;

  // Export what's currently on the screen, adapting to the active tab.
  const doExport = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    if (activeTab === "claims") {
      // Grouped by client — flatten each claim with its client group into one sheet.
      const groups = {};
      list.forEach((r) => {
        const key = r.client_name || "— Not linked —";
        (groups[key] = groups[key] || []).push(r);
      });
      const rows = [];
      Object.entries(groups).forEach(([client, claims]) => {
        claims.forEach((r) => rows.push({
          client, claim_date: r.claim_date, employee: r.user_name || "",
          expense_type: r.expense_type, project: r.project_name || "",
          amount: r.amount, status: r.status, remarks: r.action_remarks || "",
        }));
      });
      exportRowsToXlsx({
        filename: `reimbursements_claims_${stamp}.xlsx`,
        sheetName: "Claims (by client)",
        columns: [
          { key: "client", label: "Client" },
          { key: "claim_date", label: "Date" },
          { key: "employee", label: "Employee" },
          { key: "expense_type", label: "Type" },
          { key: "project", label: "Project" },
          { key: "amount", label: "Amount (₹)" },
          { key: "status", label: "Status" },
          { key: "remarks", label: "Remarks" },
        ],
        rows,
      });
    } else if (activeTab === "client") {
      // Client-wise aggregated (as shown on screen). Flatten each month for that client.
      const byClient = {};
      (summary?.client_matrix || []).forEach((r) => {
        const cid = r.client_id || r.client_name;
        if (!byClient[cid]) byClient[cid] = { client_name: r.client_name, months: [], claimed: 0, billed: 0 };
        const g = byClient[cid];
        g.claimed += r.claimed; g.billed += r.billed;
        g.months.push({ month: r.month, claimed: r.claimed, billed: r.billed, variance: r.variance });
      });
      const rows = [];
      Object.values(byClient).forEach((c) => {
        rows.push({
          client_name: c.client_name, month: "TOTAL",
          claimed: c.claimed, billed: c.billed, variance: c.claimed - c.billed,
        });
        c.months.sort((a, b) => a.month.localeCompare(b.month)).forEach((m) => rows.push({
          client_name: c.client_name, month: m.month,
          claimed: m.claimed, billed: m.billed, variance: m.variance,
        }));
      });
      exportRowsToXlsx({
        filename: `reimbursements_clientwise_${stamp}.xlsx`,
        sheetName: "Client-wise",
        columns: [
          { key: "client_name", label: "Client" },
          { key: "month", label: "Month" },
          { key: "claimed", label: "Claimed by staff (₹)" },
          { key: "billed", label: "Billed as OPE (₹)" },
          { key: "variance", label: "Variance (₹)" },
        ],
        rows,
      });
    } else if (activeTab === "nature") {
      exportRowsToXlsx({
        filename: `reimbursements_naturewise_${stamp}.xlsx`,
        sheetName: "Nature-wise",
        columns: [
          { key: "category", label: "Category" },
          { key: "count", label: "Claims" },
          { key: "amount", label: "Amount (₹)" },
          { key: "approved", label: "Approved (₹)" },
          { key: "paid", label: "Paid (₹)" },
        ],
        rows: summary?.nature || [],
      });
    } else if (activeTab === "employee") {
      exportRowsToXlsx({
        filename: `reimbursements_employeewise_${stamp}.xlsx`,
        sheetName: "Employee-wise",
        columns: [
          { key: "name", label: "Employee" },
          { key: "employee_code", label: "Code" },
          { key: "count", label: "Claims" },
          { key: "amount", label: "Total (₹)" },
          { key: "pending", label: "Pending (₹)" },
          { key: "approved", label: "Approved (₹)" },
          { key: "paid", label: "Paid (₹)" },
        ],
        rows: summary?.employee || [],
      });
    }
  };

  const load = () => Promise.all([
    api.get(`/reimbursements?scope=${scope}${userFilter ? `&user_id=${userFilter}` : ""}${clientFilter ? `&client_id=${clientFilter}` : ""}${dateParams}`).then((r) => setList(r.data)),
    api.get("/projects").then((r) => setProjects(r.data)),
    canView ? api.get("/users").then((r) => setUsers(r.data)) : Promise.resolve(),
    canView ? api.get("/clients").then((r) => setClients(r.data)) : Promise.resolve(),
  ]);
  useEffect(() => { load(); }, [scope, userFilter, clientFilter, startDate, endDate]);

  const openCreate = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditId(r.id); setOpen(true); };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File must be < 10MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, bill_path: data.path, bill_filename: data.filename }));
      toast.success("Bill uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const submit = async (e, asDraft = false) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(form.amount), distance_km: form.distance_km ? Number(form.distance_km) : null, project_id: form.project_id || null, save_as_draft: asDraft };
      if (editId) {
        await api.patch(`/reimbursements/${editId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/reimbursements", payload);
        toast.success(asDraft ? "Saved as draft" : "Submitted");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitDraft = async (id) => {
    try { await api.post(`/reimbursements/${id}/submit`); toast.success("Submitted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete claim?")) return;
    try { await api.delete(`/reimbursements/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const viewBill = async (path) => {
    try { await openBackendFile(path); }
    catch (e) { toast.error("Cannot open file"); }
  };

  const statusColor = (s) => ({
    draft: "border-slate-200 text-slate-700 bg-slate-50",
    submitted: "border-amber-200 text-amber-700 bg-amber-50",
    approved: "border-blue-200 text-blue-700 bg-blue-50",
    paid: "border-emerald-200 text-emerald-700 bg-emerald-50",
    rejected: "border-red-200 text-red-700 bg-red-50",
  }[s] || "border-slate-200");

  const exportPath = `/exports/reimbursements?scope=${scope}${userFilter ? `&user_id=${userFilter}` : ""}${clientFilter ? `&client_id=${clientFilter}` : ""}${dateParams}`;

  return (
    <div className="space-y-6 fade-in" data-testid="reimbursements-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Reimbursements</div>
          <h1 className="font-sans text-4xl mt-2">Expense claims</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <PeriodFilter
            value={period} start={startDate} end={endDate}
            onChange={({ period: p, start_date, end_date }) => { setPeriod(p); setStartDate(start_date || ""); setEndDate(end_date || ""); }}
            testId="rmb-period"
          />
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="rounded-sm w-44" data-testid="rmb-scope-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="mine">My claims</SelectItem>
              {canView && <SelectItem value="all">All claims</SelectItem>}
              {canView && <SelectItem value="pending">Pending only</SelectItem>}
              {canView && <SelectItem value="history">History</SelectItem>}
            </SelectContent>
          </Select>
          {canView && (scope === "all" || scope === "history" || scope === "pending") && (
            <Select value={userFilter || "_all"} onValueChange={(v)=>setUserFilter(v === "_all" ? "" : v)}>
              <SelectTrigger className="rounded-sm w-44" data-testid="rmb-user-filter"><SelectValue placeholder="By user"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All users</SelectItem>
                {users.map((u)=><SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {canView && (scope === "all" || scope === "history" || scope === "pending") && (
            <Select value={clientFilter || "_all"} onValueChange={(v)=>setClientFilter(v === "_all" ? "" : v)}>
              <SelectTrigger className="rounded-sm w-44" data-testid="rmb-client-filter"><SelectValue placeholder="By client"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All clients</SelectItem>
                {clients.map((c)=><SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={doExport} data-testid="rmb-export"><FileDown className="h-4 w-4 mr-2"/>Export XLSX</Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-claim-btn"><Plus className="h-4 w-4 mr-2"/>New Claim</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-sans text-2xl">{editId ? "Edit claim" : "New claim"}</DialogTitle></DialogHeader>
              <form onSubmit={(e)=>submit(e, false)} className="grid grid-cols-2 gap-4">
                <div><Label className="eyebrow">Project</Label>
                  <Select value={form.project_id || ""} onValueChange={(v)=>setForm({...form, project_id: v})}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="rmb-project-select"><SelectValue placeholder="Optional"/></SelectTrigger>
                    <SelectContent>{projects.map((p)=><SelectItem key={p.id} value={p.id}>{p.client_name} · {p.project_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">Expense Type</Label>
                  <Select value={form.expense_type} onValueChange={(v)=>setForm({...form, expense_type: v})}>
                    <SelectTrigger className="mt-2 rounded-sm"><SelectValue/></SelectTrigger>
                    <SelectContent>{EXPENSE_TYPES.map((t)=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">Amount (₹)</Label><Input type="number" min="1" step="0.01" required value={form.amount} onChange={(e)=>setForm({...form, amount: e.target.value})} className="mt-2 rounded-sm" data-testid="rmb-amount"/></div>
                <div><Label className="eyebrow">Date</Label><Input type="date" required value={form.claim_date} onChange={(e)=>setForm({...form, claim_date: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Payment Mode</Label>
                  <Select value={form.payment_mode} onValueChange={(v)=>setForm({...form, payment_mode: v})}>
                    <SelectTrigger className="mt-2 rounded-sm"><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="Cash">Cash</SelectItem><SelectItem value="Card">Card</SelectItem><SelectItem value="UPI">UPI</SelectItem><SelectItem value="Bank Transfer">Bank Transfer</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">Distance (km)</Label><Input type="number" value={form.distance_km || ""} onChange={(e)=>setForm({...form, distance_km: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">From</Label><Input value={form.from_location || ""} onChange={(e)=>setForm({...form, from_location: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">To</Label><Input value={form.to_location || ""} onChange={(e)=>setForm({...form, to_location: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div className="col-span-2"><Label className="eyebrow">Description</Label><Textarea rows={3} value={form.description || ""} onChange={(e)=>setForm({...form, description: e.target.value})} className="mt-2 rounded-sm"/></div>
                <div className="col-span-2">
                  <Label className="eyebrow">Bill / Receipt</Label>
                  <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" onChange={onPickFile} className="hidden" data-testid="rmb-file-input"/>
                  <button type="button" onClick={()=>fileRef.current?.click()} className="mt-2 w-full border-2 border-dashed border-slate-300 rounded-sm p-6 text-center hover:bg-slate-50 transition-colors" data-testid="rmb-upload-btn">
                    {uploading ? <span className="text-sm text-slate-600">Uploading...</span> :
                      form.bill_filename ? <span className="text-sm text-slate-700 inline-flex items-center gap-2"><Paperclip className="h-4 w-4"/>{form.bill_filename} <span className="text-slate-400">· change</span></span> :
                      <span className="text-sm text-slate-500 inline-flex items-center gap-2"><Upload className="h-4 w-4"/>Click to upload (PDF/JPEG/PNG · max 10MB)</span>}
                  </button>
                </div>
                <DialogFooter className="col-span-2 gap-2">
                  {!editId && <Button type="button" onClick={(e)=>submit(e, true)} disabled={busy || uploading} variant="outline" className="rounded-sm border-slate-300" data-testid="rmb-draft-btn">Save Draft</Button>}
                  <Button type="submit" disabled={busy || uploading} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="rmb-save-btn">{busy ? "Saving..." : (editId ? "Update" : "Submit")}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v !== "claims" && !summary) loadSummary(); }}>
        <TabsList>
          <TabsTrigger value="claims" data-testid="rmb-tab-claims">Claims (by client)</TabsTrigger>
          {canView && <TabsTrigger value="client" data-testid="rmb-tab-client" onClick={loadSummary}>Client-wise</TabsTrigger>}
          {canView && <TabsTrigger value="nature" data-testid="rmb-tab-nature" onClick={loadSummary}>Nature-wise</TabsTrigger>}
          {canView && <TabsTrigger value="employee" data-testid="rmb-tab-employee" onClick={loadSummary}>Employee-wise</TabsTrigger>}
        </TabsList>

        <TabsContent value="claims" className="mt-4">
          <ClaimsByClient
            list={list}
            expanded={expanded}
            toggleExpand={toggleExpand}
            canView={canView}
            userId={user.id}
            statusColor={statusColor}
            openEdit={openEdit}
            submitDraft={submitDraft}
            remove={remove}
            viewBill={viewBill}
          />
        </TabsContent>

        {canView && (
          <TabsContent value="client" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="rmb-client-tab">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <div className="eyebrow">Client-wise reimbursement tracker</div>
                  <div className="text-xs text-slate-500 mt-1">Amount claimed by employees vs amount billed to the client as OPE. Variance highlights unbilled recoveries.</div>
                </div>
                {summary && (
                  <div className="text-xs text-slate-500 space-x-3">
                    <span>Claimed: <span className="font-semibold text-slate-800">₹{(summary.totals.claimed || 0).toLocaleString("en-IN")}</span></span>
                    <span>Billed OPE: <span className="font-semibold text-slate-800">₹{(summary.totals.billed_ope || 0).toLocaleString("en-IN")}</span></span>
                    <span>Unbilled Δ: <span className={`font-semibold ${summary.totals.unbilled_variance > 0 ? "text-red-700" : "text-emerald-700"}`}>₹{(summary.totals.unbilled_variance || 0).toLocaleString("en-IN")}</span></span>
                  </div>
                )}
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                  <tr>
                    <th className="p-4 w-8"></th>
                    <th className="text-left p-4">Client</th>
                    <th className="text-right p-4">Months</th>
                    <th className="text-right p-4">Claimed by staff</th>
                    <th className="text-right p-4">Billed as OPE</th>
                    <th className="text-right p-4">Variance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(() => {
                    // Aggregate client_matrix (which is month × client) back to client-only for the main row
                    const byClient = {};
                    (summary?.client_matrix || []).forEach((r) => {
                      const cid = r.client_id || r.client_name;
                      if (!byClient[cid]) byClient[cid] = { client_id: cid, client_name: r.client_name, months: {}, claimed: 0, billed: 0 };
                      const g = byClient[cid];
                      g.claimed += r.claimed;
                      g.billed += r.billed;
                      g.months[r.month] = { claimed: r.claimed, billed: r.billed, variance: r.variance };
                    });
                    const clientRows = Object.values(byClient).map((c) => ({ ...c, variance: c.claimed - c.billed })).sort((a, b) => b.claimed - a.claimed);
                    if (clientRows.length === 0) return <tr><td colSpan={6} className="p-8 text-center text-slate-400">No data in this period.</td></tr>;
                    return clientRows.map((c) => {
                      const key = `client-agg-${c.client_id}`;
                      const isOpen = expanded.has(key);
                      const detailed = list.filter((x) => x.client_name === c.client_name || x.project_client_id === c.client_id);
                      const monthEntries = Object.entries(c.months).sort((a, b) => a[0].localeCompare(b[0]));
                      const byNature = {};
                      detailed.forEach((x) => {
                        const n = x.expense_type || "Other";
                        byNature[n] = (byNature[n] || 0) + Number(x.amount || 0);
                      });
                      const natureRows = Object.entries(byNature).sort((a, b) => b[1] - a[1]);
                      return (
                        <Fragment key={key}>
                          <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => toggleExpand(key)} data-testid={`rmb-client-agg-${c.client_id}`}>
                            <td className="p-4">{isOpen ? <ChevronDown className="h-4 w-4 text-slate-500"/> : <ChevronRight className="h-4 w-4 text-slate-400"/>}</td>
                            <td className="p-4 font-medium">{c.client_name}</td>
                            <td className="p-4 text-right text-slate-600">{monthEntries.length}</td>
                            <td className="p-4 text-right serif-num">₹{c.claimed.toLocaleString("en-IN")}</td>
                            <td className="p-4 text-right serif-num text-slate-600">₹{c.billed.toLocaleString("en-IN")}</td>
                            <td className={`p-4 text-right serif-num font-semibold ${c.variance > 0 ? "text-red-700" : c.variance < 0 ? "text-emerald-700" : "text-slate-600"}`}>₹{c.variance.toLocaleString("en-IN")}</td>
                          </tr>
                          {isOpen && (
                            <tr className="bg-slate-50">
                              <td colSpan={6} className="p-4 space-y-3">
                                <div className="grid md:grid-cols-2 gap-3">
                                  <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                                    <div className="p-3 eyebrow border-b border-slate-100">Month-wise · Claimed vs Billed</div>
                                    <table className="w-full text-sm">
                                      <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                                        <tr>
                                          <th className="text-left p-2">Month</th>
                                          <th className="text-right p-2">Claimed</th>
                                          <th className="text-right p-2">Billed</th>
                                          <th className="text-right p-2">Variance</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {monthEntries.map(([m, v]) => (
                                          <tr key={m}>
                                            <td className="p-2 font-mono text-xs">{m}</td>
                                            <td className="p-2 text-right serif-num">₹{v.claimed.toLocaleString("en-IN")}</td>
                                            <td className="p-2 text-right serif-num text-slate-600">₹{v.billed.toLocaleString("en-IN")}</td>
                                            <td className={`p-2 text-right serif-num font-semibold ${v.variance > 0 ? "text-red-700" : v.variance < 0 ? "text-emerald-700" : "text-slate-600"}`}>₹{v.variance.toLocaleString("en-IN")}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                  <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                                    <div className="p-3 eyebrow border-b border-slate-100">Nature-wise</div>
                                    <table className="w-full text-sm">
                                      <tbody className="divide-y divide-slate-100">
                                        {natureRows.length === 0 && <tr><td className="p-3 text-center text-slate-400 text-xs" colSpan={2}>No detailed claims found for this client.</td></tr>}
                                        {natureRows.map(([n, amt]) => (
                                          <tr key={n}>
                                            <td className="p-2 text-slate-700">{n}</td>
                                            <td className="p-2 text-right serif-num">₹{amt.toLocaleString("en-IN")}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                                <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                                  <div className="p-3 eyebrow border-b border-slate-100">Date-wise · Individual claims</div>
                                  <ClaimSubTable claims={detailed} showUser={canView} viewBill={viewBill} statusColor={statusColor}/>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}

        {canView && (
          <TabsContent value="nature" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="rmb-nature-tab">
              <div className="p-4 border-b border-slate-100 eyebrow">Nature-wise (Category totals)</div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                  <tr>
                    <th className="p-4 w-8"></th>
                    <th className="text-left p-4">Category</th>
                    <th className="text-right p-4">Claims</th>
                    <th className="text-right p-4">Amount</th>
                    <th className="text-right p-4">Approved</th>
                    <th className="text-right p-4">Paid</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(summary?.nature || []).length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">No data.</td></tr>}
                  {(summary?.nature || []).map((r) => {
                    const key = `n-${r.category}`;
                    const isOpen = expanded.has(key);
                    const detailed = list.filter((x) => (x.expense_type || "Other") === r.category);
                    return (
                      <Fragment key={key}>
                        <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => toggleExpand(key)} data-testid={`rmb-nature-row-${r.category}`}>
                          <td className="p-4">{isOpen ? <ChevronDown className="h-4 w-4 text-slate-500"/> : <ChevronRight className="h-4 w-4 text-slate-400"/>}</td>
                          <td className="p-4 font-medium">{r.category}</td>
                          <td className="p-4 text-right">{r.count}</td>
                          <td className="p-4 text-right serif-num">₹{r.amount.toLocaleString("en-IN")}</td>
                          <td className="p-4 text-right serif-num text-emerald-700">₹{r.approved.toLocaleString("en-IN")}</td>
                          <td className="p-4 text-right serif-num text-blue-700">₹{r.paid.toLocaleString("en-IN")}</td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-slate-50">
                            <td colSpan={6} className="p-4"><ClaimSubTable claims={detailed} showUser={canView} viewBill={viewBill} statusColor={statusColor}/></td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}

        {canView && (
          <TabsContent value="employee" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="rmb-employee-tab">
              <div className="p-4 border-b border-slate-100 eyebrow">Employee-wise summary</div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                  <tr>
                    <th className="p-4 w-8"></th>
                    <th className="text-left p-4">Employee</th>
                    <th className="text-left p-4">Code</th>
                    <th className="text-right p-4">Claims</th>
                    <th className="text-right p-4">Total</th>
                    <th className="text-right p-4">Pending</th>
                    <th className="text-right p-4">Approved</th>
                    <th className="text-right p-4">Paid</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(summary?.employee || []).length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400">No data.</td></tr>}
                  {(summary?.employee || []).map((e) => {
                    const key = `e-${e.user_id}`;
                    const isOpen = expanded.has(key);
                    const detailed = list.filter((x) => x.user_id === e.user_id);
                    return (
                      <Fragment key={key}>
                        <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => toggleExpand(key)} data-testid={`rmb-emp-row-${e.user_id}`}>
                          <td className="p-4">{isOpen ? <ChevronDown className="h-4 w-4 text-slate-500"/> : <ChevronRight className="h-4 w-4 text-slate-400"/>}</td>
                          <td className="p-4 font-medium">{e.name}</td>
                          <td className="p-4 font-mono text-xs">{e.employee_code || "—"}</td>
                          <td className="p-4 text-right">{e.count}</td>
                          <td className="p-4 text-right serif-num">₹{e.amount.toLocaleString("en-IN")}</td>
                          <td className="p-4 text-right serif-num text-amber-700">₹{e.pending.toLocaleString("en-IN")}</td>
                          <td className="p-4 text-right serif-num text-emerald-700">₹{e.approved.toLocaleString("en-IN")}</td>
                          <td className="p-4 text-right serif-num text-blue-700">₹{e.paid.toLocaleString("en-IN")}</td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-slate-50">
                            <td colSpan={8} className="p-4"><ClaimSubTable claims={detailed} showUser={false} viewBill={viewBill} statusColor={statusColor}/></td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// -------------------- Claims (grouped by client) --------------------
function ClaimsByClient({ list, expanded, toggleExpand, canView, userId, statusColor, openEdit, submitDraft, remove, viewBill }) {
  const groups = {};
  list.forEach((r) => {
    const key = r.client_name || "— Not linked to a client —";
    if (!groups[key]) groups[key] = { name: key, claims: [], amount: 0, pending: 0, approved: 0, paid: 0 };
    const g = groups[key];
    g.claims.push(r);
    const amt = Number(r.amount || 0);
    g.amount += amt;
    if (r.status === "approved") g.approved += amt;
    else if (r.status === "paid") g.paid += amt;
    else if (["draft", "submitted"].includes(r.status)) g.pending += amt;
  });
  const sorted = Object.values(groups).sort((a, b) => b.amount - a.amount);

  if (!list.length) {
    return <div className="bg-white border border-slate-200 rounded-sm p-8 text-center text-slate-500" data-testid="reimbursements-table">No claims.</div>;
  }
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="reimbursements-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
          <tr>
            <th className="p-4 w-8"></th>
            <th className="text-left p-4">Client</th>
            <th className="text-right p-4">Claims</th>
            <th className="text-right p-4">Total</th>
            <th className="text-right p-4">Pending</th>
            <th className="text-right p-4">Approved</th>
            <th className="text-right p-4">Paid</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sorted.map((g) => {
            const key = `c-${g.name}`;
            const isOpen = expanded.has(key);
            return (
              <Fragment key={key}>
                <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => toggleExpand(key)} data-testid={`rmb-claims-client-${g.name}`}>
                  <td className="p-4">{isOpen ? <ChevronDown className="h-4 w-4 text-slate-500"/> : <ChevronRight className="h-4 w-4 text-slate-400"/>}</td>
                  <td className="p-4 font-medium text-slate-800">{g.name}</td>
                  <td className="p-4 text-right">{g.claims.length}</td>
                  <td className="p-4 text-right serif-num">₹{g.amount.toLocaleString("en-IN")}</td>
                  <td className="p-4 text-right serif-num text-amber-700">₹{g.pending.toLocaleString("en-IN")}</td>
                  <td className="p-4 text-right serif-num text-emerald-700">₹{g.approved.toLocaleString("en-IN")}</td>
                  <td className="p-4 text-right serif-num text-blue-700">₹{g.paid.toLocaleString("en-IN")}</td>
                </tr>
                {isOpen && (
                  <tr className="bg-slate-50">
                    <td colSpan={7} className="p-4">
                      <ClientDrilldown claims={g.claims} canView={canView} userId={userId} statusColor={statusColor} openEdit={openEdit} submitDraft={submitDraft} remove={remove} viewBill={viewBill}/>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ClientDrilldown({ claims, canView, userId, statusColor, openEdit, submitDraft, remove, viewBill }) {
  const byMonth = {}, byNature = {};
  claims.forEach((r) => {
    const m = (r.claim_date || "").slice(0, 7) || "—";
    const n = r.expense_type || "Other";
    const amt = Number(r.amount || 0);
    byMonth[m] = (byMonth[m] || 0) + amt;
    byNature[n] = (byNature[n] || 0) + amt;
  });
  const monthRows = Object.entries(byMonth).sort((a, b) => a[0].localeCompare(b[0]));
  const natureRows = Object.entries(byNature).sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        <MiniStackTable title="Month-wise" rows={monthRows.map(([k, v]) => ({ label: k, value: v }))}/>
        <MiniStackTable title="Nature-wise" rows={natureRows.map(([k, v]) => ({ label: k, value: v }))}/>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <div className="p-3 eyebrow border-b border-slate-100">Date-wise · individual claims</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
            <tr>
              <th className="text-left p-3">Date</th>
              {canView && <th className="text-left p-3">Employee</th>}
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Project</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-left p-3">Bill</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Remarks</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[...claims].sort((a, b) => (b.claim_date || "").localeCompare(a.claim_date || "")).map((r) => (
              <tr key={r.id} className="hover:bg-white">
                <td className="p-3 font-mono text-xs">{r.claim_date}</td>
                {canView && <td className="p-3 text-slate-700">{r.user_name}</td>}
                <td className="p-3 font-medium">{r.expense_type}</td>
                <td className="p-3 text-slate-600">{r.project_name || "—"}</td>
                <td className="p-3 text-right serif-num">₹{Number(r.amount).toLocaleString("en-IN")}</td>
                <td className="p-3">{r.bill_path ? <button onClick={() => viewBill(r.bill_path)} className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-xs"><Eye className="h-3.5 w-3.5"/>View</button> : "—"}</td>
                <td className="p-3"><span className={`text-xs uppercase tracking-[0.2em] px-2 py-1 border rounded-full ${statusColor(r.status)}`}>{r.status}</span></td>
                <td className="p-3 text-xs text-slate-600 max-w-xs truncate" title={r.action_remarks || ""}>{r.action_remarks || "—"}</td>
                <td className="p-3 text-right space-x-2 whitespace-nowrap">
                  {r.user_id === userId && ["draft", "rejected", "submitted"].includes(r.status) && <button onClick={() => openEdit(r)} className="text-slate-400 hover:text-slate-900" title="Edit"><Pencil className="h-4 w-4 inline"/></button>}
                  {r.user_id === userId && ["draft", "rejected"].includes(r.status) && <button onClick={() => submitDraft(r.id)} className="text-slate-400 hover:text-emerald-700" title="Submit"><Send className="h-4 w-4 inline"/></button>}
                  {!["approved", "paid"].includes(r.status) && r.user_id === userId && <button onClick={() => remove(r.id)} className="text-slate-400 hover:text-red-600" title="Delete"><Trash2 className="h-4 w-4 inline"/></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MiniStackTable({ title, rows }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="p-3 eyebrow border-b border-slate-100">{title}</div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 && <tr><td className="p-4 text-slate-400 text-center">No data</td></tr>}
          {rows.map((r) => (
            <tr key={r.label} className="hover:bg-slate-50">
              <td className="p-3 text-slate-700">{r.label}</td>
              <td className="p-3 text-right serif-num">₹{r.value.toLocaleString("en-IN")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClaimSubTable({ claims, showUser, viewBill, statusColor }) {
  if (!claims.length) return <div className="text-xs text-slate-400 p-3 text-center">No individual claims for this row.</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
          <tr>
            <th className="text-left p-3">Date</th>
            {showUser && <th className="text-left p-3">Employee</th>}
            <th className="text-left p-3">Type</th>
            <th className="text-left p-3">Client</th>
            <th className="text-left p-3">Project</th>
            <th className="text-right p-3">Amount</th>
            <th className="text-left p-3">Bill</th>
            <th className="text-left p-3">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {[...claims].sort((a, b) => (b.claim_date || "").localeCompare(a.claim_date || "")).map((r) => (
            <tr key={r.id} className="hover:bg-white">
              <td className="p-3 font-mono text-xs">{r.claim_date}</td>
              {showUser && <td className="p-3 text-slate-700">{r.user_name}</td>}
              <td className="p-3 font-medium">{r.expense_type}</td>
              <td className="p-3 text-slate-700">{r.client_name || "—"}</td>
              <td className="p-3 text-slate-600">{r.project_name || "—"}</td>
              <td className="p-3 text-right serif-num">₹{Number(r.amount).toLocaleString("en-IN")}</td>
              <td className="p-3">{r.bill_path ? <button onClick={() => viewBill(r.bill_path)} className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-xs"><Eye className="h-3.5 w-3.5"/>View</button> : "—"}</td>
              <td className="p-3"><span className={`text-xs uppercase tracking-[0.2em] px-2 py-1 border rounded-full ${statusColor(r.status)}`}>{r.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
